import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, vModelText as _vModelText, withKeys as _withKeys, withDirectives as _withDirectives, createVNode as _createVNode, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock } from "vue"

const _hoisted_1 = { class: "space-y-6" }
const _hoisted_2 = { class: "bg-gray-50 dark:bg-gray-700/50 rounded-xl p-4" }
const _hoisted_3 = { class: "space-y-2" }
const _hoisted_4 = { class: "flex justify-between" }
const _hoisted_5 = { class: "text-sm font-medium text-gray-900 dark:text-white text-right" }
const _hoisted_6 = { class: "flex justify-between" }
const _hoisted_7 = ["title"]
const _hoisted_8 = { class: "flex justify-between" }
const _hoisted_9 = ["title"]
const _hoisted_10 = { class: "bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-xl p-4" }

import { ref, onMounted, onUnmounted } from 'vue'
import type { Exam } from '@/services/examService'
import { useToastService } from '@/services/toast'
import BaseModal from '@/components/common/BaseModal.vue'
import BaseButton from '@/components/common/BaseButton.vue'
import { Trash2 } from 'lucide-vue-next'

// Props

export default /*@__PURE__*/_defineComponent({
  __name: 'DeleteExam',
  props: {
    isVisible: { type: Boolean },
    exam: {}
  },
  emits: ["close", "success"],
  setup(__props: any, { emit: __emit }) {

const props = __props

// Emits
const emit = __emit

// Toast service
const toast = useToastService()

// State
const isDeleting = ref(false)
const confirmationText = ref('')
const isConfirmationValid = ref(false)

// Methods
const isTextTruncated = (text: string): boolean => {
  return text.length > 40
}

const truncateText = (text: string): string => {
  if (text.length > 40) {
    return text.substring(0, 40) + '...'
  }
  return text
}

const validateConfirmation = () => {
  isConfirmationValid.value = confirmationText.value.trim() === 'DELETE'
}

// Handle Enter key press
const handleKeyPress = (event: KeyboardEvent) => {
  if (event.key === 'Enter' && props.isVisible && isConfirmationValid.value && !isDeleting.value) {
    event.preventDefault()
    confirmDelete()
  }
}

const confirmDelete = () => {
  if (isDeleting.value) return
  handleDelete()
}

const handleDelete = async () => {
  if (!props.exam || !isConfirmationValid.value || isDeleting.value) return

  isDeleting.value = true

  try {
    // Import the examService dynamically to avoid circular imports
    const { examService } = await import('@/services/examService')

    // Delete the exam permanently from the database
    await examService.deleteExam(props.exam.exam_id)

    toast.success(`Exam "${props.exam.exam_name}" has been permanently removed from the system.`)

    // Emit success event
    emit('success')
    closeModal()
  } catch (error: any) {
    console.error('Error deleting exam:', error)
    const errorMessage =
      error.response?.data?.message || error.message || 'Failed to delete exam'
    toast.error('Delete Failed', errorMessage)
  } finally {
    isDeleting.value = false
  }
}

const closeModal = () => {
  // Reset state
  isDeleting.value = false
  confirmationText.value = ''
  isConfirmationValid.value = false

  emit('close')
}

// Lifecycle hooks
onMounted(() => {
  document.addEventListener('keydown', handleKeyPress)
})

onUnmounted(() => {
  document.removeEventListener('keydown', handleKeyPress)
})

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(BaseModal, {
    "is-visible": _ctx.isVisible,
    title: "Delete Exam",
    subtitle: "Deleting Exam",
    icon: _unref(Trash2),
    size: "lg",
    type: "danger",
    "show-footer": true,
    onClose: closeModal,
    role: "alertdialog",
    "aria-labelledby": "delete-exam-title",
    "aria-describedby": "delete-exam-description"
  }, {
    footer: _withCtx(() => [
      _createVNode(BaseButton, {
        text: "Cancel",
        variant: "secondary",
        disabled: isDeleting.value,
        onClick: closeModal
      }, null, 8, ["disabled"]),
      _createVNode(BaseButton, {
        text: isDeleting.value ? 'Deleting...' : 'Delete',
        variant: "danger",
        disabled: isDeleting.value || !isConfirmationValid.value,
        loading: isDeleting.value,
        onClick: confirmDelete,
        "aria-describedby": "delete-button-help"
      }, null, 8, ["text", "disabled", "loading"])
    ]),
    default: _withCtx(() => [
      _createElementVNode("div", _hoisted_1, [
        _cache[7] || (_cache[7] = _createElementVNode("div", { class: "bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-4" }, [
          _createElementVNode("div", { class: "flex items-start" }, [
            _createElementVNode("svg", {
              class: "h-6 w-6 text-red-500 mt-0.5 mr-3 flex-shrink-0",
              viewBox: "0 0 20 20",
              fill: "currentColor",
              "aria-hidden": "true"
            }, [
              _createElementVNode("path", {
                "fill-rule": "evenodd",
                d: "M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z",
                "clip-rule": "evenodd"
              })
            ]),
            _createElementVNode("div", null, [
              _createElementVNode("h4", {
                class: "text-sm font-semibold text-red-800 dark:text-red-200",
                id: "delete-exam-title"
              }, " Exam will be removed from the system "),
              _createElementVNode("p", {
                class: "text-sm text-red-700 dark:text-red-300 mt-1",
                id: "delete-exam-description"
              }, " This will permanently delete all exam content and associated data. This action can't be undone. ")
            ])
          ])
        ], -1)),
        _createElementVNode("div", _hoisted_2, [
          _cache[4] || (_cache[4] = _createElementVNode("h4", { class: "text-sm font-semibold text-gray-900 dark:text-white mb-3" }, " Exam Details ", -1)),
          _createElementVNode("div", _hoisted_3, [
            _createElementVNode("div", _hoisted_4, [
              _cache[1] || (_cache[1] = _createElementVNode("span", { class: "text-sm text-gray-600 dark:text-gray-400" }, "Exam ID:", -1)),
              _createElementVNode("span", _hoisted_5, _toDisplayString(_ctx.exam?.exam_id), 1)
            ]),
            _createElementVNode("div", _hoisted_6, [
              _cache[2] || (_cache[2] = _createElementVNode("span", { class: "text-sm text-gray-600 dark:text-gray-400" }, "Exam Name:", -1)),
              _createElementVNode("span", {
                class: "text-sm font-medium text-gray-900 dark:text-white text-right",
                title: isTextTruncated(_ctx.exam?.exam_name || '') ? _ctx.exam?.exam_name : undefined
              }, _toDisplayString(truncateText(_ctx.exam?.exam_name || '')), 9, _hoisted_7)
            ]),
            _createElementVNode("div", _hoisted_8, [
              _cache[3] || (_cache[3] = _createElementVNode("span", { class: "text-sm text-gray-600 dark:text-gray-400" }, "Lesson Name:", -1)),
              _createElementVNode("span", {
                class: "text-sm font-medium text-gray-900 dark:text-white text-right",
                title: isTextTruncated(_ctx.exam?.lesson?.name || '(No lesson)') ? (_ctx.exam?.lesson?.name || '(No lesson)') : undefined
              }, _toDisplayString(truncateText(_ctx.exam?.lesson?.name || '(No lesson)')), 9, _hoisted_9)
            ])
          ])
        ]),
        _createElementVNode("div", _hoisted_10, [
          _cache[5] || (_cache[5] = _createElementVNode("h4", { class: "text-sm font-semibold text-yellow-800 dark:text-yellow-200 mb-2" }, " Confirmation Required ", -1)),
          _cache[6] || (_cache[6] = _createElementVNode("p", { class: "text-sm text-yellow-700 dark:text-yellow-300 mb-3" }, [
            _createTextVNode(" Type "),
            _createElementVNode("strong", null, "DELETE"),
            _createTextVNode(" to confirm this action: ")
          ], -1)),
          _withDirectives(_createElementVNode("input", {
            "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((confirmationText).value = $event)),
            type: "text",
            placeholder: "Type DELETE to confirm",
            class: "w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:focus:ring-blue-400 dark:focus:border-blue-400 transition-all duration-200",
            onInput: validateConfirmation,
            onKeydown: _withKeys(handleDelete, ["enter"]),
            "aria-label": "Type DELETE to confirm deletion"
          }, null, 544), [
            [_vModelText, confirmationText.value]
          ])
        ])
      ])
    ]),
    _: 1
  }, 8, ["is-visible", "icon"]))
}
}

})